import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

const components = {
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-teal-dark underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 focus-visible:rounded-sm"
    >
      {children}
    </a>
  ),
  code: ({
    inline,
    className,
    children,
    ...props
  }: ComponentPropsWithoutRef<'code'> & { inline?: boolean }) => {
    if (inline) {
      return (
        <code
          className="rounded-md bg-paper-2 px-1.5 py-0.5 font-mono text-[13px] text-ink"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code className={`font-mono text-[13px] ${className ?? ''}`} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }: ComponentPropsWithoutRef<'pre'>) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-warm-border bg-paper-2 p-3 text-[13px] leading-relaxed">
      {children}
    </pre>
  ),
  ul: ({ children }: ComponentPropsWithoutRef<'ul'>) => (
    <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>
  ),
  ol: ({ children }: ComponentPropsWithoutRef<'ol'>) => (
    <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>
  ),
  li: ({ children }: ComponentPropsWithoutRef<'li'>) => (
    <li className="leading-relaxed marker:text-ink-3">{children}</li>
  ),
  p: ({ children }: ComponentPropsWithoutRef<'p'>) => (
    <p className="my-2 leading-relaxed">{children}</p>
  ),
  h1: ({ children }: ComponentPropsWithoutRef<'h1'>) => (
    <h1 className="mb-2 mt-4 text-[20px] font-semibold tracking-[-0.01em] text-ink">
      {children}
    </h1>
  ),
  h2: ({ children }: ComponentPropsWithoutRef<'h2'>) => (
    <h2 className="mb-2 mt-3 text-[18px] font-semibold tracking-[-0.01em] text-ink">
      {children}
    </h2>
  ),
  h3: ({ children }: ComponentPropsWithoutRef<'h3'>) => (
    <h3 className="mb-1.5 mt-3 text-[16px] font-semibold text-ink">{children}</h3>
  ),
  hr: () => <hr className="my-3 border-warm-border" />,
  strong: ({ children }: ComponentPropsWithoutRef<'strong'>) => (
    <strong className="font-semibold text-ink">{children}</strong>
  ),
  em: ({ children }: ComponentPropsWithoutRef<'em'>) => (
    <em className="italic text-ink">{children}</em>
  ),
  blockquote: ({ children }: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="my-3 border-l-2 border-warm-border-strong pl-3 italic text-ink-2">
      {children}
    </blockquote>
  ),
  table: ({ children }: ComponentPropsWithoutRef<'table'>) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-warm-border">
      <table className="w-full text-[13px]">{children}</table>
    </div>
  ),
  thead: ({ children }: ComponentPropsWithoutRef<'thead'>) => (
    <thead className="bg-paper-2 text-ink-2">{children}</thead>
  ),
  th: ({ children }: ComponentPropsWithoutRef<'th'>) => (
    <th className="border-b border-warm-border px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }: ComponentPropsWithoutRef<'td'>) => (
    <td className="border-b border-warm-border px-3 py-2 align-top">{children}</td>
  ),
};

export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    <div
      className={`text-[15px] leading-relaxed text-ink [&>:first-child]:mt-0 [&>:last-child]:mb-0 ${className ?? ''}`}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
